// @flow

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import {
  ModalContextActions,
  NavContextActions,
  useModalDispatch,
  useModalState,
  useNavDispatch,
  useNavState,
  usePageState,
} from 'contexts';
import { Color, flex, hoverChild, Layout, maxWidthContainer, Width, Transition, absolute } from 'styles';
import { Link } from 'components/Gui';
import { Logo as LogoData } from 'data/global/nav';
import { Logo, LogoMarkets } from 'components/Svg';
import MenuButton from './MenuButton';
import { LIGHT_DARK } from 'Constants';

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  height: ${Layout.NAV_HEIGHT_MOBILE}px;
  background: ${({ noBg, menuOpen, theme, scrollingUp, atTop }) =>
    noBg || (!menuOpen && atTop && !scrollingUp && theme === LIGHT_DARK.DARK) ? 'transparent' : Color.WHITE};
  z-index: ${Layout.Z_INDEX_MOBILE_NAV_BAR};
  ${flex()};
  transition: transform ${Transition.DEFAULT_MOBILE};
  transform: translateY(${({ visible }) => (visible ? 0 : -Layout.NAV_HEIGHT_MOBILE)}px);
  pointer-events: none;

  @media print, screen and (min-width: ${Width.BREAK_MOBILE_NAV}px) {
    display: none;
  }

  > div {
    &:nth-of-type(1) {
      ${maxWidthContainer()};
      ${flex('flex-start', 'center')};

      svg {
        width: 68px;
        ${({ theme, atTop, menuOpen }) =>
          theme === LIGHT_DARK.DARK && atTop && !menuOpen
            ? css`
                ${hoverChild('path', 'fill', Color.WHITE, Color.BLURPLE_LIGHT)};
              `
            : css`
                ${hoverChild('path', 'fill', Color.BLURPLE_MOODY, Color.PURPLE_FIGURE)};
              `};
      }
    }
  }
`;

const MenuButtonWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${Layout.NAV_HEIGHT_MOBILE}px;
  z-index: ${Layout.Z_INDEX_MOBILE_MENU_BUTTON};
  ${flex()};
  transition: transform ${Transition.DEFAULT_MOBILE};
  transform: translateY(${({ visible }) => (visible ? 0 : -Layout.NAV_HEIGHT_MOBILE)}px);
  pointer-events: none;

  @media print, screen and (min-width: ${Width.BREAK_MOBILE_NAV}px) {
    display: none;
  }

  > div {
    ${maxWidthContainer()};
    ${flex('flex-end', 'center')};
  }
`;

const StaticWrapper = styled.div`
  ${absolute(0, 0, 'unset', 0)};
  height: 100px;

  > div {
    height: 100%;
    position: relative;
    z-index: 1;
    ${maxWidthContainer(Width.XL + 100)};
    ${flex()};

    a {
      svg {
        width: 100px;
        ${hoverChild('path', 'fill', Color.BLURPLE_MOODY, Color.PURPLE_FIGURE)};
      }
    }
  }
`;

const Line = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 1px;
  ${flex()};

  div {
    ${maxWidthContainer()};
    background: ${Color.BLURPLE_LIGHT};
    height: 1px;
    transform-origin: 50% 50% 0;
    transition: transform ${Transition.DEFAULT_MOBILE};
    transform: scaleX(${({ visible }) => (visible ? 1 : 0)});
  }
`;

const MobileNavBar = () => {
  const {
    headerConfig: {
      toggleHidden: { background, logo, nav, staticLogo },
      theme,
    },
  } = usePageState();
  const { isOpen } = useModalState();
  const { renderMobile, atTop, scrollingUp, showingMobileNav } = useNavState();
  const dispatch = useNavDispatch();
  const modalDispatch = useModalDispatch();
  const logoClicked = useCallback(() => {
    if (isOpen) modalDispatch({ type: ModalContextActions.CLOSE });
    if (showingMobileNav) dispatch({ type: NavContextActions.TOGGLE_MOBILE_NAV });
  }, [dispatch, isOpen, modalDispatch, showingMobileNav]);
  const visible = showingMobileNav || atTop || scrollingUp ? 1 : 0;
  return useMemo(
    () =>
      renderMobile ? (
        <>
          {staticLogo && (
            <>
              <Wrapper
                visible={visible}
                menuOpen={showingMobileNav || isOpen}
                scrollingUp={scrollingUp}
                atTop={atTop}
                theme={theme}
                noBg={background}
                aria-hidden={true}>
                <div>
                  {!logo && (
                    <Link url={'/'} {...LogoData} onClick={logoClicked}>
                      {LogoMarkets}
                    </Link>
                  )}
                </div>
                <Line visible={showingMobileNav}>
                  <div />
                </Line>
              </Wrapper>
              <MenuButtonWrapper visible={visible}>
                <div>{!nav && <MenuButton />}</div>
              </MenuButtonWrapper>
            </>
          )}
          {!staticLogo && (
            <StaticWrapper>
              <div>
                <Link {...LogoData}>{Logo}</Link>
              </div>
            </StaticWrapper>
          )}
        </>
      ) : null,
    [logoClicked, renderMobile, visible, showingMobileNav, logo, nav, background, staticLogo, isOpen, theme, atTop, scrollingUp]
  );
};

export default MobileNavBar;
